import React from 'react';
import { SparkJob } from '../../types/sparkTypes';
import { formatEta, getJobDisplayStatus, useAnimatedProgress } from '../../hooks/useAnimatedProgress';
import styles from './JobProgressBar.module.css';

export interface JobProgressBarProps {
  job: SparkJob;
  variant: 'board' | 'panel' | 'indicator';
  hideEta?: boolean;
  hideStatus?: boolean;
}

function getStatusTone(status: SparkJob['_status']): 'running' | 'completed' | 'failed' | 'cancelled' {
  if (status === 'completed') {
    return 'completed';
  }
  if (status === 'failed') {
    return 'failed';
  }
  if (status === 'cancelled') {
    return 'cancelled';
  }
  return 'running';
}

function getStatusLabel(status: SparkJob['_status'], progressPct: number | null): string | null {
  if (status === 'completed') {
    return 'Completed';
  }
  if (status === 'failed') {
    return 'Failed';
  }
  if (status === 'cancelled') {
    return 'Cancelled';
  }
  if (progressPct != null && progressPct > 0 && status === 'running') {
    return `${Math.round(progressPct)}% complete`;
  }
  if (status === 'pending') {
    return 'Pending';
  }
  if (status === 'running') {
    return 'In progress';
  }
  return null;
}

export const JobProgressBar: React.FC<JobProgressBarProps> = ({
  job,
  variant,
  hideEta = variant === 'board',
  hideStatus = variant === 'board',
}) => {
  const { progressPct, remainingSeconds } = useAnimatedProgress(job);
  const displayStatus = getJobDisplayStatus(job) || job._status;

  let normalizedProgress = progressPct;
  if (displayStatus === 'completed') {
    normalizedProgress = 100;
  } else if ((displayStatus === 'pending' || displayStatus === 'running') && normalizedProgress == null && variant !== 'board') {
    normalizedProgress = displayStatus === 'pending' ? 8 : 12;
  }

  if (normalizedProgress == null || normalizedProgress <= 0) {
    return null;
  }

  const tone = getStatusTone(displayStatus);
  const etaLabel = hideEta ? null : formatEta(remainingSeconds);
  const statusLabel = hideStatus ? null : getStatusLabel(displayStatus, normalizedProgress);

  return (
    <div className={`${styles.wrapper} ${styles[`variant-${variant}`]}`}>
      <div className={styles.track}>
        <div
          className={`${styles.fill} ${styles[`tone-${tone}`]}`}
          style={{ width: `${Math.min(100, Math.max(normalizedProgress, 0))}%` }}
        />
      </div>
      {(statusLabel || etaLabel) && (
        <div className={styles.meta}>
          <span className={styles.status}>{statusLabel}</span>
          <span className={styles.eta}>{etaLabel}</span>
        </div>
      )}
    </div>
  );
};
